import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import {
  BriefCreator,
  ResultsDirection,
  ResultsSort,
  ResultsStat,
  resultsCsv,
  resultsQueryParams,
  parseResultsQuery,
  scanResults,
} from './results-scan';
import { MonPostDrawer } from '../mon-post-drawer/mon-post-drawer';


@Component({
  selector: 'mbtm-mon-results',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MonPostDrawer],
  templateUrl: './mon-results.html',
})
export class MonResults implements OnDestroy {
  readonly rows = input<ResultsStat[] | null>(null);
  readonly creators = input<readonly BriefCreator[]>([]);
  readonly briefTitle = input('Monitoring brief');

  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly skeletonTotals = [1, 2, 3, 4, 5, 6, 7];
  readonly sortOptions: { id: ResultsSort; label: string }[] = [
    { id: 'posted', label: 'Posted date' },
    { id: 'followers', label: 'Followers' },
    { id: 'views', label: 'Views' },
    { id: 'likes', label: 'Likes' },
    { id: 'comments', label: 'Comments' },
    { id: 'shares', label: 'Shares' },
    { id: 'saves', label: 'Saves' },
    { id: 'engagement', label: 'Engagement' },
    { id: 'rate', label: 'Engagement rate' },
  ];

  private readonly subscriptionList = new Subscription();
  private readonly searchInput = new Subject<string>();

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastNotificationService = inject(ToastNotificationService);

  readonly searchDraft = signal(this.route.snapshot.queryParamMap.get('search') ?? '');
  readonly openedPostId = signal<string | null>(null);
  readonly query = toSignal(this.route.queryParamMap.pipe(map((params) => parseResultsQuery(params))), {
    initialValue: parseResultsQuery(this.route.snapshot.queryParamMap),
  });

  readonly loading = computed(() => this.rows() === null);
  readonly scan = computed(() => scanResults(this.rows() ?? [], this.creators(), this.query()));

  constructor() {
    this.subscriptionList.add(
      this.searchInput.pipe(debounceTime(350), distinctUntilChanged()).subscribe((search) => {
        this.updateQuery({ search, page: 1 });
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchDraft.set(value);
    this.searchInput.next(value);
  }

  onCreator(event: Event): void {
    this.updateQuery({ creatorId: (event.target as HTMLSelectElement).value, page: 1 });
  }

  onSort(event: Event): void {
    this.updateQuery({ sort: (event.target as HTMLSelectElement).value as ResultsSort, page: 1 });
  }

  onDirection(event: Event): void {
    this.updateQuery({ direction: (event.target as HTMLSelectElement).value as ResultsDirection, page: 1 });
  }

  showPage(page: number): void {
    this.updateQuery({ page });
  }

  openPost(socialPostId: string): void {
    this.openedPostId.set(socialPostId);
  }

  clearFilters(): void {
    this.searchDraft.set('');
    this.updateQuery({ search: '', creatorId: '', sort: 'views', direction: 'desc', page: 1 });
  }

  exportResults(): void {
    const scan = this.scan();
    if (scan.status !== 'list') {
      return;
    }
    const file = resultsCsv(scan.matched, this.briefTitle());
    const url = URL.createObjectURL(new Blob([file.csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.fileName;
    anchor.click();
    URL.revokeObjectURL(url);
    const posts = file.count === 1 ? '1 post' : `${file.count} posts`;
    void this.toastNotificationService.alert({
      title: 'Results exported',
      message: `Exported ${posts}.`,
      type: 'success',
      icon: 'success',
    });
  }

  rateText(value: number | null): string {
    if (value === null) {
      return '—';
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  private updateQuery(patch: Partial<ReturnType<typeof parseResultsQuery>>): void {
    const next = { ...this.query(), ...patch };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: resultsQueryParams(next),
      replaceUrl: true,
    });
  }

}
